// src/components/ui/ErrorPopup.js
/**
 * Generic modal popup used for errors, warnings, or confirmation prompts.
 *
 * Props:
 *   • `popup`  – {
 *       title?: React.ReactNode,
 *       message: React.ReactNode,
 *       onConfirm?: Function,
 *       onDismiss?: Function,
 *       confirmLabel?: string,
 *       cancelLabel?: string,
 *       confirmIcon?: string,
 *       dialogLabel?: string,
 *     } | null
 *   • `onClose` – () => void   (called when user dismisses the popup)
 *
 * Design:
 *   • Renders nothing when `popup` is null.
 *   • If `popup.onConfirm` is provided, shows “Proceed / Cancel” buttons;
 *     otherwise shows a single “OK” button.
 *   • Blocks background interaction with a semi-transparent overlay.
 *
 * Tailwind CSS classes assume the project’s existing style tokens
 * (primary-button, outlined-button, custom-container, etc.).
 */

import React, { useCallback, useEffect } from "react";

const ErrorPopup = ({ popup, onClose }) => {
  /* ------------------------------------------------------------------ */
  /* Close on ESC key — hook must run every render (rule-of-hooks)      */
  /* ------------------------------------------------------------------ */
  const handleDismiss = useCallback(async () => {
    onClose();

    if (typeof popup?.onDismiss === "function") {
      try {
        await popup.onDismiss();
      } catch (err) {
        console.error("ErrorPopup onDismiss error:", err);
      }
    }
  }, [onClose, popup]);

  useEffect(() => {
    if (!popup) return; // Do nothing when popup is null
    const handleKey = (e) => {
      if (e.key === "Escape") {
        void handleDismiss();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleDismiss, popup]);

  // Early-exit render after hooks have run.
  if (!popup) return null;

  const {
    title = null,
    message,
    onConfirm,
    confirmLabel = "Proceed",
    cancelLabel = "Cancel",
    confirmIcon = "",
    dialogLabel = "Error popup",
  } = popup;

  /* ------------------------------------------------------------------ */
  /* Helper triggers                                                    */
  /* ------------------------------------------------------------------ */
  const handleConfirm = async () => {
    // Close this popup first to avoid race conditions with new popups
    onClose();

    if (typeof onConfirm === "function") {
      try {
        await onConfirm();
      } catch (err) {
        // Swallow errors; parent logic will handle via new popups
        console.error("ErrorPopup onConfirm error:", err);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div
      className="fixed inset-0 flex items-start justify-center bg-black/40 z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={dialogLabel}
      onClick={handleDismiss} // close on backdrop click
    >
      <div
        className="custom-container p-4 rounded shadow flex flex-col gap-4 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()} // stop inside clicks bubbling
      >
        {title ? <h2 className="subtitle-text text-left">{title}</h2> : null}

        {/* Message */}
        <div className="whitespace-pre-line text-left">{message}</div>

        {/* Action buttons */}
        {onConfirm ? (
          <div className="flex justify-between gap-2">
            <button
              type="button"
              className="outlined-button"
              onClick={handleDismiss}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className="primary-button !flex-row gap-1.5"
              onClick={handleConfirm}
            >
              {confirmIcon ? (
                <span
                  className="material-symbols-outlined text-[20px] leading-none"
                  aria-hidden="true"
                >
                  {confirmIcon}
                </span>
              ) : null}
              <span>{confirmLabel}</span>
            </button>
          </div>
        ) : (
          <div className="flex justify-end">
            <button
              type="button"
              className="primary-button"
              onClick={handleDismiss}
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ErrorPopup);
